import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

const emptyHeroSlideForm = () => ({
  title: '',
  subtitle: '',
  description: '',
  badge: '',
  accent: '#d44100',
  buttonText: '',
  buttonAction: '',
  secondaryButtonText: '',
  secondaryButtonAction: '',
  gradient: 'from-[#d44100] to-[#0f4c5c]',
  gradientEnabled: true,
  textColor: '#ffffff',
  buttonStyle: 'primary',
  animationType: 'fade',
  order: 0,
  isActive: true,
});

/** Owns the hero slides list, stats, and the shared Add/Edit form. */
export default function useHeroSlides({ activeTab, showNotification }) {
  const [heroSlides, setHeroSlides] = useState([]);
  const [loadingHeroSlides, setLoadingHeroSlides] = useState(false);
  const [heroSlideStats, setHeroSlideStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [editingHeroSlide, setEditingHeroSlide] = useState(null);
  const [showAddHeroSlideModal, setShowAddHeroSlideModal] = useState(false);
  const [heroSlideForm, setHeroSlideForm] = useState(emptyHeroSlideForm());

  const fetchHeroSlides = async () => {
    setLoadingHeroSlides(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/hero-slides/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setHeroSlides(await response.json());
    } catch (error) {
      console.error('Error fetching hero slides:', error);
    } finally {
      setLoadingHeroSlides(false);
    }
  };

  const fetchHeroSlideStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/hero-slides/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) setHeroSlideStats(await response.json());
    } catch (error) {
      console.error('Error fetching hero slide stats:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'hero-slides') {
      fetchHeroSlides();
      fetchHeroSlideStats();
    }
  }, [activeTab]);

  const openAddModal = () => {
    setHeroSlideForm(emptyHeroSlideForm());
    setShowAddHeroSlideModal(true);
  };

  const closeAddModal = () => setShowAddHeroSlideModal(false);

  const openEditModal = (slide) => {
    setHeroSlideForm({
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      description: slide.description || '',
      badge: slide.badge || '',
      accent: slide.accent || '#d44100',
      buttonText: slide.buttonText || '',
      buttonAction: slide.buttonAction || '',
      secondaryButtonText: slide.secondaryButtonText || '',
      secondaryButtonAction: slide.secondaryButtonAction || '',
      gradient: slide.gradient || 'from-[#d44100] to-[#0f4c5c]',
      gradientEnabled: slide.gradientEnabled !== undefined ? slide.gradientEnabled : true,
      textColor: slide.textColor || '#ffffff',
      buttonStyle: slide.buttonStyle || 'primary',
      animationType: slide.animationType || 'fade',
      order: slide.order || 0,
      isActive: slide.isActive !== undefined ? slide.isActive : true,
    });
    setEditingHeroSlide(slide);
  };

  const closeEditModal = () => setEditingHeroSlide(null);

  const handleAddHeroSlide = async (formData) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/hero-slides/admin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        showNotification('Hero slide created successfully', 'success');
        closeAddModal();
        fetchHeroSlides();
        fetchHeroSlideStats();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to create hero slide', 'error');
      }
    } catch (error) {
      console.error('Error creating hero slide:', error);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const handleUpdateHeroSlide = async (slideId, formData) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/hero-slides/admin/${slideId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        showNotification('Hero slide updated successfully', 'success');
        closeEditModal();
        fetchHeroSlides();
        fetchHeroSlideStats();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to update hero slide', 'error');
      }
    } catch (error) {
      console.error('Error updating hero slide:', error);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const handleDeleteHeroSlide = async (slideId, slideTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${slideTitle}"? This action cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/hero-slides/admin/${slideId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        showNotification('Hero slide deleted successfully', 'success');
        fetchHeroSlides();
        fetchHeroSlideStats();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to delete hero slide', 'error');
      }
    } catch (error) {
      console.error('Error deleting hero slide:', error);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const handleToggleHeroSlide = async (slideId, isActive) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/hero-slides/admin/${slideId}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        showNotification(`Hero slide ${!isActive ? 'activated' : 'deactivated'} successfully`, 'success');
        fetchHeroSlides();
        fetchHeroSlideStats();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to toggle hero slide', 'error');
      }
    } catch (error) {
      console.error('Error toggling hero slide:', error);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  // Shared submit handler for both Add and Edit forms — dispatches based on
  // whether a slide is currently being edited.
  const handleHeroSlideSubmit = async (e) => {
    e.preventDefault();
    const fileInput = e.target.querySelector('input[type="file"][name="image"]');
    const imageFile = fileInput ? fileInput.files[0] : null;

    const formData = new FormData();
    Object.keys(heroSlideForm).forEach((key) => {
      if (heroSlideForm[key] !== null && heroSlideForm[key] !== undefined) {
        formData.append(key, heroSlideForm[key]);
      }
    });
    if (imageFile) formData.append('image', imageFile);

    if (editingHeroSlide) {
      await handleUpdateHeroSlide(editingHeroSlide.id, formData);
    } else {
      await handleAddHeroSlide(formData);
    }

    setHeroSlideForm(emptyHeroSlideForm());
    if (fileInput) fileInput.value = '';
  };

  return {
    heroSlides, loadingHeroSlides, heroSlideStats,
    showAddHeroSlideModal, openAddModal, closeAddModal,
    editingHeroSlide, openEditModal, closeEditModal,
    heroSlideForm, setHeroSlideForm, handleHeroSlideSubmit,
    handleDeleteHeroSlide, handleToggleHeroSlide,
  };
}
